/**
 * waypointPickerStore — module-singleton state for the waypoint-based body-motion
 * generation flow (mirrors navSettingsStore's pattern: plain `state`, a `Set` of
 * listeners, `emit()`). Shared between the wizard DOM panel (list editing, route
 * status) and the in-Canvas WaypointPickerOverlay (armed click-to-place, numbered
 * markers + planned path line). Both react to the same waypoint list + route plan.
 */
let state = {
  armed: false,               // viewport click-to-place is live
  waypoints: [],              // [{ x, z, snapped, drift }] — planar; y resolved at generate time
  pathMode: 'navmesh',        // 'navmesh' | 'straight' (straight is an EXPLICIT user mode, never a fallback)
  pickError: null,            // last rejected viewport pick (string | null)
  route: null,                // cold plan for overlay/status: { mode, path, segments, length } | { error } | null
}
const listeners = new Set()
function emit() {
  for (const fn of listeners) fn()
}

export const waypointPickerStore = {
  getState() { return state },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },
  arm() { state = { ...state, armed: true }; emit() },
  disarm() { state = { ...state, armed: false, pickError: null }; emit() },
  setPathMode(mode) { state = { ...state, pathMode: mode }; emit() },
  addWaypoint(wp) { state = { ...state, waypoints: [...state.waypoints, wp] }; emit() },
  removeWaypoint(i) {
    state = { ...state, waypoints: state.waypoints.filter((_, k) => k !== i) }
    emit()
  },
  moveWaypoint(i, dir) {
    const j = i + dir
    if (j < 0 || j >= state.waypoints.length) return // clamp at both ends — no-op
    const next = state.waypoints.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    state = { ...state, waypoints: next }
    emit()
  },
  clear() { state = { ...state, waypoints: [], route: null, pickError: null }; emit() },
  setWaypoints(list) { state = { ...state, waypoints: list }; emit() },
  setRoute(route) { state = { ...state, route }; emit() },
  setPickError(msg) { state = { ...state, pickError: msg }; emit() },
}

if (typeof window !== 'undefined') window.__WAYPOINT_PICKER__ = waypointPickerStore
